import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../lib/errors';
import { LEAD_STAGE_LABELS, ACTIVITY_TYPE_ICONS, ACTIVITY_TYPE_LABELS } from '../types';

interface FollowUpEvent {
  id: string;
  kind: 'lead' | 'activity';
  lead_id: number;
  activity_id?: number;
  lead_name?: string | null;
  title: string;
  company?: string | null;
  phone?: string | null;
  email?: string | null;
  value?: number | null;
  stage?: string;
  type?: string;
  salesperson?: string | null;
  date: string;
  done: boolean;
}

interface CalendarioProps {
  password: string;
  refreshKey: number;
  onChanged: () => void;
}

const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const pad = (n: number) => String(n).padStart(2, '0');

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toDateKey(iso: string): string {
  return dateKey(new Date(iso));
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function buildMonth(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export default function Calendario({ password, refreshKey, onChanged }: CalendarioProps) {
  const [events, setEvents] = useState<FollowUpEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState<string | null>(null);
  const [monthCursor, setMonthCursor] = useState<Date>(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedKey, setSelectedKey] = useState<string>(dateKey(new Date()));

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-calendar', {
        body: { action: 'list', password },
      });
      if (invokeErr) {
        const msgErr = await extractErrorMessage(invokeErr);
        throw new Error(msgErr);
      }
      const parsed = data as { events?: FollowUpEvent[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setEvents(parsed?.events ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar o calendário.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const todayKey = dateKey(new Date());

  const eventsByKey = useMemo(() => {
    const map = new Map<string, FollowUpEvent[]>();
    events.forEach((e) => {
      const k = toDateKey(e.date);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(e);
    });
    return map;
  }, [events]);

  const metrics = useMemo(() => {
    const overdue = events.filter((e) => toDateKey(e.date) < todayKey && !e.done).length;
    const today = events.filter((e) => toDateKey(e.date) === todayKey && !e.done).length;
    const next7Key = dateKey(addDays(new Date(), 7));
    const next7 = events.filter((e) => {
      const k = toDateKey(e.date);
      return k >= todayKey && k <= next7Key && !e.done;
    }).length;
    const deliveries = events.filter((e) => e.kind === 'delivery').length;
    return { overdue, today, next7, total: events.length, deliveries };
  }, [events, todayKey]);

  const cells = buildMonth(monthCursor.getFullYear(), monthCursor.getMonth());
  const weeks: (Date | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  const monthLabel = monthCursor.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  const selectedEvents = eventsByKey.get(selectedKey) ?? [];
  const selectedDateLabel = (() => {
    const d = new Date(`${selectedKey}T12:00:00`);
    return d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  })();

  const changeMonth = (delta: number) => {
    setMonthCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const goToday = () => {
    const d = new Date();
    setMonthCursor(new Date(d.getFullYear(), d.getMonth(), 1));
    setSelectedKey(dateKey(d));
  };

  const complete = async (ev: FollowUpEvent) => {
    setCompleting(ev.id);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-calendar', {
        body: {
          action: 'complete',
          password,
          kind: ev.kind,
          id: ev.kind === 'lead' ? ev.lead_id : ev.kind === 'delivery' ? ev.delivery_id : ev.activity_id,
        },
      });
      if (invokeErr) {
        const msgErr = await extractErrorMessage(invokeErr);
        throw new Error(msgErr);
      }
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao concluir follow-up.');
    } finally {
      setCompleting(null);
    }
  };

  const summaryCards = [
    { label: 'Follow-ups ativos', value: metrics.total, icon: 'ri-calendar-check-line', tone: 'bg-orange-50 text-orange-600' },
    { label: 'Atrasados', value: metrics.overdue, icon: 'ri-alarm-warning-line', tone: 'bg-red-50 text-red-600' },
    { label: 'Hoje', value: metrics.today, icon: 'ri-sun-line', tone: 'bg-amber-50 text-amber-600' },
    { label: 'Próximos 7 dias', value: metrics.next7, icon: 'ri-calendar-todo-line', tone: 'bg-green-50 text-green-600' },
    { label: 'Entregas', value: metrics.deliveries, icon: 'ri-truck-line', tone: 'bg-blue-50 text-blue-600' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
            <i className="ri-calendar-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Calendário de Follow-ups</h2>
            <p className="text-sm text-gray-500">Sua rotina comercial, dia a dia</p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Atualizar
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {summaryCards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-hop-border p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${c.tone}`}>
                <i className={`${c.icon} text-lg`}></i>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">{c.label}</p>
                <p className="text-2xl font-bold text-gray-900">{c.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* Calendário */}
        <div className="flex-1 w-full bg-white rounded-xl border border-hop-border p-4 md:p-5">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h3 className="text-base font-bold text-gray-900 capitalize">{monthLabel}</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => changeMonth(-1)}
                className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button
                onClick={goToday}
                className="text-xs font-semibold text-[#E8610A] bg-[#E8610A]/10 hover:bg-[#E8610A]/20 px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
              >
                Hoje
              </button>
              <button
                onClick={() => changeMonth(1)}
                className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-px mb-1">
            {DAY_NAMES.map((d) => (
              <div key={d} className="text-center text-[11px] font-semibold text-gray-400 uppercase py-2">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-px bg-hop-border border border-hop-border rounded-lg overflow-hidden">
            {weeks.map((week, wi) => (
              <div key={wi} className="contents">
                {week.map((day, di) => {
                  if (!day) {
                    return (
                      <div key={di} className="min-h-[92px] bg-hop-bg-alt/50" />
                    );
                  }
                  const key = dateKey(day);
                  const dayEvents = eventsByKey.get(key) ?? [];
                  const isToday = key === todayKey;
                  const isSelected = key === selectedKey;
                  const isCurrentMonth = day.getMonth() === monthCursor.getMonth();
                  const hasOverdue = dayEvents.some((e) => toDateKey(e.date) < todayKey && !e.done);

                  return (
                    <button
                      key={di}
                      onClick={() => setSelectedKey(key)}
                      className={`min-h-[92px] p-1.5 text-left flex flex-col cursor-pointer transition-colors ${
                        isSelected ? 'bg-[#E8610A]/10' : isToday ? 'bg-amber-50/60' : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      <span
                        className={`inline-flex items-center justify-center w-6 h-6 text-xs font-semibold rounded-full mb-1 ${
                          isToday
                            ? 'bg-[#E8610A] text-white'
                            : isCurrentMonth
                            ? 'text-gray-700'
                            : 'text-gray-300'
                        }`}
                      >
                        {day.getDate()}
                      </span>
                      <div className="space-y-1 w-full overflow-hidden">
                        {dayEvents.slice(0, 2).map((e) => (
                          <span
                            key={e.id}
                            className={`block text-[10px] leading-tight truncate px-1.5 py-0.5 rounded ${
                              e.kind === 'lead'
                                ? hasOverdue && toDateKey(e.date) < todayKey
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-[#E8610A]/10 text-[#E8610A]'
                                : e.kind === 'delivery'
                                ? 'bg-blue-50 text-blue-700'
                                : 'bg-blue-50 text-blue-700'
                            }`}
                          >
                            {e.kind === 'lead' ? e.title : e.kind === 'delivery' ? `🚚 ${e.title}` : `• ${e.title}`}
                          </span>
                        ))}
                        {dayEvents.length > 2 && (
                          <span className="block text-[10px] text-gray-400 px-1">
                            +{dayEvents.length - 2} mais
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
              <span className="w-2.5 h-2.5 rounded-full bg-[#E8610A]"></span> Follow-up de lead
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-400"></span> Lembrete/atividade
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-600"></span> Entrega
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span> Atrasado
            </span>
          </div>
        </div>

        {/* Detalhes do dia */}
        <div className="w-full lg:w-80 shrink-0 bg-white rounded-xl border border-hop-border p-4 md:p-5">
          <h4 className="font-bold text-gray-900 capitalize mb-1">{selectedDateLabel}</h4>
          <p className="text-xs text-gray-500 mb-4">
            {selectedEvents.length === 0
              ? 'Nenhum follow-up para este dia.'
              : `${selectedEvents.length} follow-up${selectedEvents.length === 1 ? '' : 's'} agendado${selectedEvents.length === 1 ? '' : 's'}`}
          </p>

          {selectedEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-100 text-gray-400 rounded-full mb-3">
                <i className="ri-calendar-line text-xl"></i>
              </div>
              <p className="text-sm text-gray-400">Tudo em dia por aqui.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {selectedEvents.map((ev) => {
                const isOverdue = toDateKey(ev.date) < todayKey && !ev.done;
                return (
                  <div
                    key={ev.id}
                    className={`border rounded-xl p-3 ${
                      isOverdue ? 'border-red-200 bg-red-50/50' : 'border-hop-border'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {ev.kind === 'lead' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2 py-0.5">
                              <i className="ri-user-line"></i>
                              {ev.stage ? LEAD_STAGE_LABELS[ev.stage] ?? ev.stage : 'Lead'}
                            </span>
                          ) : ev.kind === 'delivery' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5">
                              <i className="ri-truck-line"></i>
                              Entrega {ev.status === 'entregue' ? '— Entregue' : '— Pendente'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5">
                              <i className={ACTIVITY_TYPE_ICONS[ev.type ?? ''] ?? 'ri-sticky-note-line'}></i>
                              {ACTIVITY_TYPE_LABELS[ev.type ?? ''] ?? ev.type ?? 'Atividade'}
                            </span>
                          )}
                          {isOverdue && (
                            <span className="text-[10px] font-semibold text-red-600 whitespace-nowrap">
                              Atrasado
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-semibold text-gray-900">
                          {ev.kind === 'lead' ? ev.title : ev.kind === 'delivery' ? ev.title : ev.lead_name ?? 'Sem lead vinculado'}
                        </p>
                        {ev.kind === 'activity' && (
                          <p className="text-xs text-gray-600 mt-0.5">{ev.title}</p>
                        )}
                        {ev.kind === 'delivery' && ev.items && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{String(ev.items)}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          <i className="ri-time-line"></i> {formatTime(ev.date)}
                          {ev.phone ? ` · ${ev.phone}` : ''}
                        </p>
                        {ev.value != null && ev.kind === 'lead' && (
                          <p className="text-xs font-semibold text-gray-700 mt-0.5">{formatBRL(Number(ev.value))}</p>
                        )}
                      </div>
                    </div>
                    {!ev.done && (
                      <button
                        onClick={() => complete(ev)}
                        disabled={completing === ev.id}
                        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg px-3 py-1.5 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
                      >
                        {completing === ev.id ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            Concluindo...
                          </>
                        ) : (
                          <>
                            <i className="ri-check-line"></i>
                            {ev.kind === 'delivery' ? 'Marcar entregue' : 'Concluir'}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}